import React, { useContext, useEffect, useState, useCallback } from 'react';
import { DataLayerEventContext } from '../contexts/dataLayerEventContext';
import ErrorScreen from '../components/lm-d-s/errorScreen';
import { ScreensContext } from '../contexts/screensContext';
import WelcomeScreen from '../components/26ua-prl-s/welcome-screen';
import PreWelcomeAgeVerification from '../components/26ua-prl-s/pre-welcome-age-verification';
import NotAllowed from '../components/26ua-prl-s/not-allowed';
import NewPackCharacteristicsScreen from '../components/26ua-prl-s/new-pack-characteristics-screen';
import ReviewFlowScreen from '../components/26ua-prl-s/review-flow-screen';
import PageFlowWrapper from '../common/registration-flow-wrapper';
import CookieConsentFlowWrapper from '../common/cookie-consent-flow-wrapper';
import BrandLineScreen from '../common/brand-line-screen';
import AllBrandsScreen from '../common/all-brands-screen';

const UaPrlS = () => {
  const isTargetCampaign =
    window.location.href.includes('24UA-PRL-S') ||
    sessionStorage.getItem('utm_campaign') === '24UA-PRL-S' ||
    sessionStorage.getItem('isQrCode') === '24UA-PRL-S';

  const [instanceId] = useState(() => Math.random().toString(36).substring(7));

  const [screenNumber, setScreenNumber] = useState(0);

  const [historyState, setHistoryState] = useState({});

  const [brandLine, setBrandLine] = useState('parliament');

  useEffect(() => {
    if (!isTargetCampaign) return;

    const handlePopState = (event) => {
      const state = event.state;
      if (
        state &&
        state.instanceId === instanceId &&
        typeof state.screenNumber === 'number'
      ) {
        setScreenNumber(state.screenNumber);
        setHistoryState(state);
        if (state.brandLine) {
          setBrandLine(state.brandLine);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    const initialState = {
      screenNumber: 0,
      brandLine: 'parliament',
      instanceId: instanceId
    };
    window.history.replaceState(initialState, '', window.location.href);
    setHistoryState(initialState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isTargetCampaign, instanceId]);

  const handleSetScreenNumber = useCallback(
    (number, isBack = false, extraState = {}) => {
      setScreenNumber(number);
      if (isTargetCampaign) {
        const newState = {
          ...window.history.state,
          screenNumber: number,
          instanceId: instanceId,
          ...extraState
        };
        setHistoryState(newState);
        if (extraState.brandLine) {
          setBrandLine(extraState.brandLine);
        }
        if (isBack) {
          window.history.replaceState(newState, '', window.location.href);
        } else {
          window.history.pushState(newState, '', window.location.href);
        }
      }
    },
    [isTargetCampaign, instanceId]
  );

  const handleUpdateHistoryState = useCallback(
    (extraState) => {
      if (isTargetCampaign) {
        const newState = {
          ...window.history.state,
          instanceId: instanceId,
          ...extraState
        };
        setHistoryState(newState);
        window.history.pushState(newState, '', window.location.href);
      }
    },
    [isTargetCampaign, instanceId]
  );

  const [isDisplayedAll, setIsDisplayedAll] = useState(false);
  const [endSessionType, setEndSessionType] = useState('SESSION_END');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const skipWrapper = user?.AV_Status && user?.CDA_Status;

  const { init } = useContext(DataLayerEventContext);

  useEffect(() => {
    init('24UA-PRL-S');
    sessionStorage.setItem('unsubscription_source', '24UA-PRL-S');
  }, [init]);

  const getScreen = (number, cb) => {
    switch (number) {
      case 0: {
        return (
          <PreWelcomeAgeVerification
            handleNextButtonClick={() => handleSetScreenNumber(2)}
            handleNotAllowed={() => handleSetScreenNumber(1)}
          />
        );
      }
      case 1:
        return <NotAllowed />;
      case 2:
        return (
          <WelcomeScreen handleToProduct={() => handleSetScreenNumber(3)} />
        );
      case 3: {
        const content = (
          <NewPackCharacteristicsScreen
            handleToReview={() => {
              handleSetScreenNumber(4);
            }}
            handleToBrandLinePage={() => {
              handleSetScreenNumber(5);
            }}
          />
        );
        return skipWrapper ? (
          content
        ) : (
          <PageFlowWrapper
            isDarkMode
            withoutCookieModal
            externalScreenNumber={historyState.innerScreenNumber}
            setExternalScreenNumber={(val) =>
              handleSetScreenNumber(3, false, { innerScreenNumber: val })
            }
          >
            {content}
          </PageFlowWrapper>
        );
      }
      case 4:
        return (
          <ReviewFlowScreen
            isDarkMode
            setScreenNumber={handleSetScreenNumber}
            backToMenuButtonClick={() => handleSetScreenNumber(3, true)}
            innerScreenNumber={historyState.innerScreenNumber}
            setInnerScreenNumber={(val) =>
              handleSetScreenNumber(4, false, { innerScreenNumber: val })
            }
          />
        );
      case 5: {
        return (
          <BrandLineScreen
            brandId={brandLine}
            setScreenNumber={handleSetScreenNumber}
            backButtonClick={() => handleSetScreenNumber(6, true)}
            nextButtonClick={() => handleSetScreenNumber(3)}
            withoutHeader
            historyState={historyState}
            updateHistoryState={handleUpdateHistoryState}
          />
        );
      }
      case 6: {
        return (
          <AllBrandsScreen
            setScreenNumber={handleSetScreenNumber}
            setBrandLine={(brand) => {
              handleSetScreenNumber(5, false, { brandLine: brand });
            }}
            handleBackButton={() => handleSetScreenNumber(3, true)}
            withoutHeader
            historyState={historyState}
            updateHistoryState={handleUpdateHistoryState}
          />
        );
      }
      default: {
        return <ErrorScreen setScreenNumber={handleSetScreenNumber} />;
      }
    }
  };

  return (
    <ScreensContext.Provider
      value={{
        endSessionType,
        setEndSessionType,
        isDisplayedAll,
        setIsDisplayedAll
      }}
    >
      <CookieConsentFlowWrapper>
        {getScreen(screenNumber, handleSetScreenNumber)}
      </CookieConsentFlowWrapper>
    </ScreensContext.Provider>
  );
};

export default UaPrlS;
